import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { BidFormComponent } from '../bid-form/bid-form.component';
import { NlCurrencyPipe } from '../pipes/nl-currency.pipe';
import { NlDatePipe } from '../pipes/nl-date.pipe';
import { VehicleService } from '../../services/vehicle.service';
import { Vehicle } from '../../models/vehicle.model';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-bid-modal',
  imports: [IconComponent, BidFormComponent, DecimalPipe, NlDatePipe, NlCurrencyPipe],
  templateUrl: './bid-modal.component.html',
  styleUrl: './bid-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BidModalComponent {
  public readonly vs = inject(VehicleService);
  public readonly closing = signal(false);

  private readonly deliveryDate = computed<DateTime | null>(() => {
    const dateStr = this.vs.currentBidVehicle()?.dateDelivery?.date;
    if (!dateStr) return null;
    let dt = DateTime.fromISO(dateStr);
    if (!dt.isValid) dt = DateTime.fromSQL(dateStr);
    return dt.isValid ? dt : null;
  });

  public readonly daysUntilDelivery = computed<number | null>(() => {
    const dt = this.deliveryDate();
    if (!dt) return null;
    const days = Math.ceil(dt.diff(DateTime.now(), 'days').days);
    return Math.max(0, days);
  });

  public get hasNext(): boolean {
    const v = this.vs.currentBidVehicle();
    if (!v) return false;
    const list = this.vs.vehicles();
    const idx = list.findIndex(item => item.id === v.id);
    return idx !== -1 && idx < list.length - 1;
  }

  public onBidPlaced(): void {
    // Refresh the list so the new bid is reflected in the table/cards
    this.vs.reloadSilent();
    this.vs.closeBidModal();
  }

  public onBidPlacedAndNext(vehicle: Vehicle): void {
    const list = this.vs.vehicles();
    const idx = list.findIndex(item => item.id === vehicle.id);
    if (idx !== -1 && idx < list.length - 1) {
      this.vs.currentBidVehicle.set(list[idx + 1]);
    } else {
      this.vs.closeBidModal();
    }
    // The reload re-points currentBidVehicle at the refreshed vehicle object
    this.vs.reloadSilent();
  }

  public close(): void {
    if (this.closing()) return;
    this.closing.set(true);
    setTimeout(() => {
      this.vs.closeBidModal();
      this.closing.set(false);
    }, 200);
  }
}
